import { ICreateLikeData, IMapyBoxItem, IMapyboxItemUpdateEvent, TActivityType, TNoteVersion } from "~/interfaces";
import MyMapsDialog, { IMyMapsDialog } from "./mymaps-dialog";
import { _ } from "~/i18n";
import { createMyMapsReplaceItem } from "../mymaps/replace-item";
import { createMyMapsInput } from "../mymaps/input";
import { createMyMapsNote } from "../mymaps/note";
import { folderCopy, folderCreate, folderUpdate, likeCopy, likeCreate, likeUpdate, setFolderData, trackUpdate } from "~/util/providers/mapybox";
import { getMapyboxError, getMapyboxItemAnalyticsType, getUserTime, hasVisitedOption, IAnalyticsEdit, mapyboxAnalyticsEdit, mapyboxNotification, updateUserTime } from "~/util/mapybox";
import { MAPYBOX_MAX_DATE, MAPYBOX_MIN_DATE, MAX_TITLE_LEN } from "./const";
import { store } from "~/state/store";
import ActivitySelect from "~/ui/activities/select";
import MyMapsFolderSelectCreate from "../mymaps/folder-select-create";
import { checkNoteVersion } from "~/util/user-note";
import { MAPYBOX_ITEM_UPDATE_EVENT_NAME } from "../mymaps/const";
import { openPropositionDialog } from "~/util/premium";
import DateSelect from "~/ui/dateselect";
import { createMyMapsToggle } from "~/ui/mymaps/toggle";

interface IState {
	usertitle: string;
	note: string;
	trackType: TActivityType;
	alwaysOnMap?: boolean;
	/* eslint-disable camelcase */
	user_time: number;
	/* eslint-enable camelcase */
	visited: boolean;
}

interface IFolderData {
	id: string;
	title: string;
}

interface IFolderState {
	folderId: string;
	createNewFolder: boolean;
	newFolderTitle: string;
	folderTitle: string;
}

interface IMapyBoxItemSaveData {
	likeData: Partial<IMapyBoxItem>;
	folderData: IFolderData;
	trackType?: TActivityType;
}

interface IMyMapsMapyBoxItem extends Pick<IMyMapsDialog, "onCancel" | "onClose"> {
	mode: "create" | "edit" | "copy" | "replace" | "save-as-new";
	createData?: ICreateLikeData;
	likeData?: IMapyBoxItem;
	title?: string;
	folder?: {
		id: string;
		title: string;
	};
	isPremiumUser?: boolean;
	noteVersion?: TNoteVersion;
	onCreateFolder?: () => void;
	onSave?: (data: IMapyBoxItemSaveData) => void;
	onError?: () => void;
}

function mapyboxAnalyticsGetChanged(item: IMapyBoxItem, origData: IState, changedState: Partial<IMapyBoxItem>, folderState: IFolderState, trackTypeChange: boolean) {
	const analyticsEditData: IAnalyticsEdit = {
		origin: "detail_box_edit",
		type: getMapyboxItemAnalyticsType(item),
		changed: [],
	};

	if ("usertitle" in changedState && item.usertitle && changedState.usertitle !== origData.usertitle) {
		analyticsEditData.changed.push("name");
	}

	if (folderState.createNewFolder || folderState.folderId !== item.parent_id) {
		analyticsEditData.changed.push("folder");
	}

	if ("description" in changedState && changedState.description !== origData.note) {
		analyticsEditData.changed.push("note");
		analyticsEditData.note = !!changedState.description;
	}

	if ("visited" in changedState && changedState.visited !== origData.visited) {
		analyticsEditData.changed.push("visited");
		analyticsEditData.visited = changedState.visited;
	}

	if (trackTypeChange) {
		analyticsEditData.changed.push("track_type");
	}

	return analyticsEditData;
}


const DEFAULT_TRACK_TYPE = "pesky";

export default class MyMapsMapyBoxItem {
	_opts: IMyMapsMapyBoxItem;

	_isReplace: boolean;

	_dialog: MyMapsDialog;

	_state: IState;

	_origState: IState;

	_changed: boolean;

	_focusElement: HTMLTextAreaElement | HTMLInputElement;

	_folderSelectCreate: MyMapsFolderSelectCreate;

	_activitySelect: ActivitySelect;

	constructor(data: IMyMapsMapyBoxItem) {
		this._opts = {
			mode: "create",
			title: "",
			onSave: () => {},
			onCancel: () => {},
			onClose: () => {},
			onCreateFolder: () => {},
			onError: () => {},
			isPremiumUser: false,
			noteVersion: "version1",
			...data,
		};
		this._isReplace = this._opts.mode === "replace";
		this._state = {
			usertitle: "",
			note: "",
			visited: false,
			/* eslint-disable camelcase */
			user_time: Date.now(),
			/* eslint-enable camelcase */
			trackType: DEFAULT_TRACK_TYPE,
			...this._opts.createData
				? {
					usertitle: this._opts.createData.usertitle || this._opts.createData.title || this._opts.title,
					note: this._opts.createData.description || "",
					/* eslint-disable camelcase */
					user_time: getUserTime(this._opts.createData),
					/* eslint-enable camelcase */
					...this._opts.createData.type === "path" && this._opts.createData.trackType
						? {
							trackType: this._opts.createData.trackType,
						}
						: {},
				}
				: {},
			...this._opts.likeData
				? {
					usertitle: this._opts.likeData.usertitle || this._opts.likeData.title || this._opts.title,
					note: this._opts.likeData.description || "",
					/* eslint-disable camelcase */
					user_time: getUserTime(this._opts.likeData),
					/* eslint-enable camelcase */
					trackType: (this._opts.likeData.type === "path" || (this._opts.likeData.type === "measure" && this._opts.likeData.data?.readonly) ? this._opts.likeData.data?.info?.trackType : this._opts.likeData.data?.type) || DEFAULT_TRACK_TYPE,
					...this._opts.likeData.type === "folder" ? { alwaysOnMap: this._opts.likeData.always_on_map || false } : {},
					visited: this._opts.likeData.visited || false,
				}
				: {},
		};
		// omezeni titulku
		this._state.usertitle = this._state.usertitle.slice(0, MAX_TITLE_LEN).trim();
		this._origState = window.structuredClone(this._state);
		this._changed = false;
		this._focusElement = null;
		this._folderSelectCreate = null;

		const { user } = store.getState().user;

		if (user.state === "green") {
			this._build();
		}
	}

	open() {
		const { user } = store.getState().user;
		const open = () => {
			this._dialog?.open();
			!this._isReplace && this._focusElement?.focus();
		};

		if (user.state === "green") {
			open();
		} else {
			Mapy.getComponent("login").open({
				analyticsData: {
					loginOrigin: "save",
				},
				motivation: "save",
			}).then(() => {
				this._build();
				open();
			}, () => {});
		}
	}

	destroy() {
		this._dialog?.destroy();
		this._activitySelect?.destroy();
	}

	_closeDialog(force?: boolean) {
		this._opts.onClose();
		this._dialog.close(force);
	}

	_getTitles(): Array<string> {
		if (this._isReplace) {
			return [_("mymap.saveDetail.updateBtn"), _("save")];
		} else if (this._opts.mode === "edit") {
			return [_("mymap.contextMenu.editProps")];
		}

		return [_("save")];
	}

	_build() {
		const dialog = new MyMapsDialog({
			titles: {
				main: this._getTitles(),
				saveBtn: _("save"),
				cancelBtn: _("cancel"),
			},
			onThrowAwayTest: () => this._checkClose(),
			onThrowAwayConfirm: () => {
				this._closeDialog(true);
			},
			onSave: () => {
				this._onSave();
			},
			onCancel: () => {
				this._opts.onCancel();
				this._closeDialog(true);
			},
		});
		const contents = dialog.contents;

		let mainContent: HTMLDivElement = null;

		if (this._isReplace) {
			const replaceCont = contents[0];

			replaceCont.classList.add("mymaps__mapyboxItemDialog__replaceContent");
			replaceCont.append(createMyMapsReplaceItem({
				data: this._opts.likeData,
			}));
			mainContent = contents[1];
		} else {
			mainContent = contents[0];
		}

		mainContent.classList.add(this._isReplace ? "mymaps__mapyboxItemDialog__mainContentReplaced" : "mymaps-dialog__defContent");

		// vybiratko typu
		if (
			(this._opts.mode === "edit" && (this._opts.likeData?.type === "path" || (this._opts.likeData?.type === "link" && this._opts.likeData?.linktype === "track") || (this._opts.likeData?.type === "measure" && this._opts.likeData?.data?.readonly)))
			|| (this._opts.mode === "create" && this._opts.createData?.type === "path")
			|| (this._opts.mode === "save-as-new" && this._opts.likeData?.type === "path")
		) {
			this._activitySelect = new ActivitySelect({
				selected: this._state.trackType,
				onChange: trackType => {
					// pri editaci by nemelo byt mozne zadat all, ale kdyby se to nahodou stalo, tak to nezaneseme do dat a na server
					if (trackType === "all") {
						return;
					}

					this._updateState({ trackType });
				},
			});
			mainContent.append(this._activitySelect.container);
		}

		// titulek
		const titleInput = createMyMapsInput({
			placeholder: _("mymap.addPopup.name"),
			bold: true,
			onChange: usertitle => {
				this._updateState({ usertitle });
			},
			text: this._state.usertitle,
			count: MAX_TITLE_LEN,
		});

		mainContent.append(titleInput.container);
		this._focusElement = titleInput.input;

		// nove pouze u kopirovani a ulozit jako novy, nahrada
		if (["copy", "save-as-new", "create", "replace"].includes(this._opts.mode)) {
			this._folderSelectCreate = new MyMapsFolderSelectCreate({
				// nevybirame slozku, neprednastavujeme - chceme treba ulozit i do stejne slozky
				disabled: this._opts.mode === "copy" && this._opts.createData?.type === "folder",
				folder: this._opts.folder
					? this._opts.folder
					: undefined,
				onChange: () => {
					this._updateState({});
				},
			});

			if (!(this._opts.mode === "copy" && this._opts.createData?.type === "folder")) {
				mainContent.append(this._folderSelectCreate.container);
			}
		}

		// datum
		if (this._hasUserTime()) {
			const dateSelect = new DateSelect({
				date: new Date(this._state.user_time),
				minDate: MAPYBOX_MIN_DATE,
				maxDate: MAPYBOX_MAX_DATE,
			});

			dateSelect.node.addEventListener("datechange", (event: CustomEvent) => {
				// eslint-disable-next-line camelcase
				this._updateState({ user_time: updateUserTime(event.detail.date) });
			});
			mainContent.append(dateSelect.node);
		}

		// poznamka
		if (this._opts.isPremiumUser || checkNoteVersion(this._opts.noteVersion, "version2")) {
			const noteData = createMyMapsNote({
				text: this._state.note,
				isPremiumUser: this._opts.isPremiumUser,
				noteVersion: this._opts.noteVersion,
				onChange: note => {
					this._updateState({ note });
				},
			});

			mainContent.append(noteData.container);

			if (this._opts.isPremiumUser) {
				this._focusElement = noteData.textarea;
			}
		}

		// zaskrtavatko pro always on map u slozek
		if (this._opts.likeData?.type === "folder") {
			mainContent.appendChild(createMyMapsToggle({
				text: _("mymap.folders.alwaysOnMap"),
				checked: this._opts.isPremiumUser ? this._state.alwaysOnMap || false : false,
				isPremiumUser: this._opts.isPremiumUser,
				onChange: alwaysOnMap => {
					this._updateState({ alwaysOnMap });
				},
				onClick: () => {
					if (!this._opts.isPremiumUser) {
						this._closeDialog(true);
						openPropositionDialog("MyMapsNotes");
					}
				},
			}));
		}

		if (Mapy.CUS.mapybox?.visited && (hasVisitedOption(this._opts.createData) || hasVisitedOption(this._opts.likeData))) {
			mainContent.appendChild(createMyMapsToggle({
				text: !this._opts.isPremiumUser && this._state.visited
					? _("stateSwitch.items2.visit")
					: _("mymap.visited.title"),
				// pro neprihlasene vzdy false!
				checked: this._opts.isPremiumUser
					? this._state.visited || false
					: false,
				isPremiumUser: this._opts.isPremiumUser,
				onChange: visited => {
					this._updateState({ visited });
				},
				onClick: () => {
					if (!this._opts.isPremiumUser) {
						this._closeDialog(true);
						Mapy.getComponent("logger").log("setVisited_premiumPurchase_show");
						openPropositionDialog("setVisited");
					}
				},
			}));
		}

		this._dialog = dialog;
	}

	_updateState(newData: Partial<IState>) {
		Object.assign(this._state, newData);

		this._dialog.setSaveBtnTitle = this._folderSelectCreate?.state.createNewFolder ? _("mymap.folders.createAndSave") : _("save");

		this._changed = this._folderSelectCreate?.changed
			|| this._state.note !== this._origState.note
			|| this._state.usertitle !== this._origState.usertitle
			|| this._state.trackType !== this._origState.trackType
			/* eslint-disable camelcase */
			|| this._state.user_time !== this._origState.user_time
			/* eslint-enable camelcase */
			|| this._state.alwaysOnMap !== this._origState.alwaysOnMap
			|| this._state.visited !== this._origState.visited;
	}

	_checkClose() {
		return !this._changed;
	}

	_sendUpdateSignal(newData: Partial<IMapyBoxItem>) {
		window.dispatchEvent(new CustomEvent(MAPYBOX_ITEM_UPDATE_EVENT_NAME, {
			detail: {
				id: this._opts.likeData.id,
				source: "edit-props-dialog",
				data: {
					...this._opts.likeData,
					...newData,
				},
			} as IMapyboxItemUpdateEvent,
		}));
	}

	_sendError(errorMsg: string) {
		this._dialog.error = errorMsg;
		this._dialog.loading = false;
		this._opts.onError();
	}

	_getFolderState(): IFolderState {
		if (this._folderSelectCreate) {
			return this._folderSelectCreate.state;
		}

		return {
			folderId: this._opts.folder?.id || this._opts.likeData?.parent_id || "",
			createNewFolder: false,
			newFolderTitle: "",
			folderTitle: this._opts.folder?.title || this._opts.likeData?.parent_name || "",
		};
	}

	// copy + save as new
	async _createNewItem({
		folderId,
		folderTitle,
		itemId,
		type = "item",
	}: {
		folderId: string;
		folderTitle: string;
		itemId: string;
		type: "folder" | "item",
	}) {
		const output: {
			newLikeData: Partial<IMapyBoxItem>;
			error: string;
		} = {
			newLikeData: null,
			error: "",
		};

		// copy | save-as-new
		try {
			if (type === "folder") {
				const folderData = {
					title: this._state.usertitle,
					description: this._state.note,
				};
				const folderCopyData = await folderCopy(itemId, "", folderData);

				output.newLikeData = {
					id: folderCopyData.copied[0].new_id,
					...folderData,
				};

				mapyboxNotification({
					item: {
						...output.newLikeData,
						type: "folder",
					},
					operation: "folder-copy",
					folderId: output.newLikeData.id,
					folderTitle: this._state.usertitle,
				});
			} else {
				// vytvorime
				const likeCopyData = await likeCopy([itemId], folderId);
				const newLikeId = likeCopyData.copied.filter(item => "new_id" in item)[0].new_id;
				const hasUserTime = this._hasUserTime();
				const updateData = {
					...(this._opts.likeData?.title || this._opts.createData?.title || "") === this._state.usertitle
						? {}
						: {
							usertitle: this._state.usertitle,
						},
					description: this._state.note,
					visited: this._state.visited,
					// eslint-disable-next-line camelcase
					...hasUserTime ? { user_time: this._state.user_time } : {},
				};

				// aktualizujeme
				await likeUpdate(newLikeId, updateData);

				output.newLikeData = {
					id: newLikeId,
					...updateData,
				};

				mapyboxNotification({
					item: {
						...this._opts.createData,
						...this._opts.likeData,
						...output.newLikeData,
					},
					operation: "like-copy",
					folderTitle,
					folderId,
				});
			}
		} catch (exc) {
			output.error = getMapyboxError({
				operation: type === "folder"
					? "folder-copy"
					: "like-copy",
			});
		}

		return output;
	}

	_hasTrackType() {
		return this._opts.likeData && (this._opts.likeData.type === "path" || (this._opts.likeData.type === "link" && this._opts.likeData.linktype === "track") || (this._opts.likeData.type === "measure" && this._opts.likeData.data?.readonly));
	}

	_hasUserTime() {
		if (!Mapy.CUS.mapybox?.date) {
			return false;
		}

		const sources = [this._opts.createData, this._opts.likeData];

		for (const dataSource of sources) {
			if (!dataSource) {
				continue;
			}

			if (dataSource.type === "path"
				|| dataSource.type === "route"
				|| dataSource.type === "track"
				|| (dataSource.type === "link" && dataSource.linktype === "track")
				|| (dataSource.type === "measure" && dataSource.data?.readonly)
			) {
				return true;
			}
		}

		return false;
	}

	async _onSave() {
		// neni zmena, zrusime
		if (!this._changed && this._opts.likeData && !["replace", "copy", "edit", "save-as-new"].includes(this._opts.mode)) {
			this._closeDialog(true);

			return;
		}

		this._dialog.error = "";
		this._dialog.loading = true;

		const folderState = this._getFolderState();
		let folderId = folderState.folderId;
		let newLikeData: Partial<IMapyBoxItem> = null;
		const hasTrackType = this._hasTrackType();
		const hasUserTime = this._hasUserTime();
		let trackType: TActivityType = hasTrackType
			? this._state.trackType
			: null;
		let analyticsEditData: IAnalyticsEdit = null;

		// nova slozka
		if (folderState.createNewFolder) {
			if (folderState.newFolderTitle === "") {
				this._dialog.loading = false;
				this._dialog.error = _("mymap.folders.emptyFolder");

				return;
			}

			try {
				const createData = await folderCreate(folderState.newFolderTitle);

				folderId = createData.id;

				// ulozime - pro pripad override
				if (this._folderSelectCreate) {
					this._folderSelectCreate.overwriteOrigState(folderId);
				}
			} catch (exc) {
				this._sendError(getMapyboxError({
					operation: "folder-create",
				}));

				return;
			}
		}

		// kopie like/slozka
		if (this._opts.mode === "copy" || this._opts.mode === "save-as-new") {
			const createData = await this._createNewItem({
				folderId,
				folderTitle: folderState.folderTitle,
				itemId: this._opts.mode === "save-as-new"
					? this._opts.likeData.id
					: this._opts.createData.id,
				type: this._opts.createData?.type === "folder"
					? "folder"
					: "item",
			});

			if (createData.error) {
				this._sendError(createData.error);

				return;
			}

			newLikeData = createData.newLikeData;
		// mame data - nahrazeni
		} else if (this._opts.likeData && this._isReplace && this._dialog.activeContent === 0) {
			try {
				const hints = {};
				const updateData = structuredClone(this._opts.createData);

				// tady se data nahrazuji - nemame moznost cokoliv menit
				if (updateData.title === updateData.usertitle) {
					delete updateData.usertitle;
				}

				if ("mark" in updateData) {
					hints["1.mark.lon"] = "float";
					hints["1.mark.lat"] = "float";
				}

				await likeUpdate(this._opts.likeData.id, updateData, hints);

				newLikeData = {
					// data muzeme pouzit ze stavu
					id: this._opts.likeData.id,
					usertitle: this._state.usertitle,
					description: this._state.note,
					visited: this._state.visited,
					// eslint-disable-next-line camelcase
					...hasUserTime ? { user_time: this._state.user_time } : {},
					...hasTrackType
						? {
							trackType: this._state.trackType,
						}
						: {},
				};

				mapyboxNotification({
					item: {
						...this._opts.likeData,
						...newLikeData,
					},
					operation: "like-copy",
					folderId,
					folderTitle: folderState.folderTitle,
				});
			} catch (exc) {
				this._sendError(_("mymap.saveDetail.updateError"));

				return;
			}
		// mame data - update dat
		} else if (this._opts.likeData && !this._isReplace) {
			try {
				let usertitle = this._state.usertitle;
				let updateData: Partial<IMapyBoxItem> = {};
				let trackTypeChange = false;

				if (this._opts.likeData.type === "folder") {
					updateData = {
						title: this._state.usertitle,
						description: this._state.note,
						// eslint-disable-next-line camelcase
						always_on_map: this._state.alwaysOnMap,
					};

					await folderUpdate(this._opts.likeData.id, updateData);

					folderState.folderTitle = this._state.usertitle;
				} else {
					if (this._state.trackType !== this._origState.trackType && this._opts.likeData.type === "link" && this._opts.likeData.linktype === "track") {
						await trackUpdate(this._opts.likeData.linkid, {
							// @ts-ignore
							"data.info.trackType": this._state.trackType,
						});
						trackTypeChange = true;
					}

					// update
					usertitle = this._opts.likeData.title === this._state.usertitle
						? ""
						: this._state.usertitle;
					updateData = {
						usertitle,
						description: this._state.note,
						visited: this._state.visited,
						// eslint-disable-next-line camelcase
						...hasUserTime ? { user_time: this._state.user_time } : {},
						...this._opts.likeData.type === "path" || (this._opts.likeData.type === "measure" && this._opts.likeData.data?.readonly)
							? {
								// @ts-ignore
								"data.info.trackType": this._state.trackType,
							}
							: {},
					};

					if (updateData["data.info.trackType"]) {
						trackTypeChange = this._state.trackType !== this._origState.trackType;
					}

					await likeUpdate(this._opts.likeData.id, updateData);

					trackType = this._state.trackType;
				}

				newLikeData = {
					id: this._opts.likeData.id,
					...updateData,
				};
				analyticsEditData = mapyboxAnalyticsGetChanged(this._opts.likeData, this._origState, updateData, folderState, trackTypeChange);

				mapyboxNotification({
					item: {
						...this._opts.likeData,
						...newLikeData,
					},
					operation: "edit",
					folderId,
					folderTitle: folderState.folderTitle,
				});
			} catch (exc) {
				this._sendError(_("mymap.notifications.edit.error"));

				return;
			}
		// vytvoreni
		} else {
			try {
				const createData = {
					...this._opts.createData,
					usertitle: this._state.usertitle,
					description: this._state.note,
					visited: this._state.visited,
					// eslint-disable-next-line camelcase
					...hasUserTime ? { user_time: this._state.user_time } : {},
					...folderId
						? {
							// eslint-disable-next-line camelcase
							parent_id: folderId,
						}
						: {},
				};
				const hints = {};

				// smazeme usertitle
				if (createData.title === createData.usertitle) {
					delete createData.usertitle;
				}

				if ("mark" in createData) {
					hints["0.mark.lon"] = "float";
					hints["0.mark.lat"] = "float";
				}

				// bacha - vraci pouze id, ne data! (vsechny create metody nad batuzkem)
				const likeCreateData = await likeCreate(createData, hints);

				newLikeData = {
					...createData,
					id: likeCreateData.id,
				};

				if (this._opts.mode === "create" && this._opts.createData.type === "path") {
					await likeUpdate(newLikeData.id, {
						// @ts-ignore
						"data.info.trackType": this._state.trackType,
					});

					trackType = this._state.trackType;
				}

				Mapy.getComponent("places").notifyLikeCreate(folderId);

				const { data: detailData } = store.getState().detail;

				if (detailData) {
					// eslint-disable-next-line camelcase
					Mapy.getComponent("places").addToLikeList(newLikeData.id, { parent_name: folderState.folderTitle, parent_id: folderId, source: detailData.source, id: detailData.id });
				}

				mapyboxNotification({
					item: newLikeData,
					operation: "like-copy",
					folderId,
					folderTitle: folderState.folderTitle,
				});
			} catch (exc) {
				this._sendError(_("mymap.addPopup.createError"));

				return;
			}
		}

		setFolderData({
			id: folderId,
			title: folderState.folderTitle,
		});

		// analytika
		if (analyticsEditData) {
			mapyboxAnalyticsEdit({
				...analyticsEditData,
				alwaysOnMap: this._state.alwaysOnMap,
			});
		}

		this._opts.onSave({
			likeData: newLikeData,
			folderData: {
				id: folderId,
				title: folderState.folderTitle,
			},
			trackType,
		});
		this._closeDialog(true);

		// signal
		if (this._opts.mode === "edit") {
			this._sendUpdateSignal(newLikeData);
		}
	}
}
